import { useState, useEffect, useMemo } from 'react'
import { FiCheck, FiAlertTriangle } from 'react-icons/fi'
import { FaTshirt } from 'react-icons/fa'
import api from '../../../utils/api'
import Select from 'react-select'
import SelectStyles from '../../../shared/css/SelectStyle'
import Input from '../../../shared/components/form/input'
import Button from '../../../shared/components/Button/Button'
import FormLayout from '../../../shared/components/FormLayout/FormLayout'
import useFlashMessage from '../../../hooks/useFlashMessage'
import NumberToMoney from '../../../shared/functions/NumberToMoney'
import getCostForCloth from '../../../shared/functions/getCostForCloth'
import './ApplyCostToColors.css'

// Custo é por (cor, tecido) — a mesma cor pode estar marcada em "Tecido A" sem estar em
// "Tecido B". `cloth: null` é o balde "sem tecido" (grupo sintético 'sem-tecido' na tela).
function clothSlotsFor(item) {
    return item.cloths && item.cloths.length > 0 ? item.cloths : [{ _id: 'sem-tecido', name: 'Sem tecido' }]
}
function pairKey(colorId, clothId) {
    return `${colorId}|${clothId}`
}

function ApplyCostToColors({ cost, providerId, btCancelAction, handleOnSuccess }) {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()

    const [colors, setColors] = useState([])
    const [cloths, setCloths] = useState([])
    const [colorTypes, setColorTypes] = useState([])
    const [loading, setLoading] = useState(true)
    const [checked, setChecked] = useState(new Set())
    const [initiallyChecked, setInitiallyChecked] = useState(new Set())
    const [saving, setSaving] = useState(false)
    const [search, setSearch] = useState('')
    const [clothFilter, setClothFilter] = useState(null)
    const [colorTypeFilter, setColorTypeFilter] = useState(null)
    const [onlyNoCost, setOnlyNoCost] = useState(false)

    useEffect(() => {
        api.get('color/getall', {
            params: { provider: providerId },
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            if (response.data) {
                const fetchedColors = response.data.colors
                setColors(fetchedColors)
                setCloths(response.data.cloths)
                setColorTypes(response.data.colorTypes)

                const already = new Set()
                fetchedColors.forEach((item) => {
                    clothSlotsFor(item).forEach((cloth) => {
                        const clothIdForLookup = cloth._id === 'sem-tecido' ? null : cloth._id
                        if (getCostForCloth(item, clothIdForLookup).some((c) => c._id === cost._id)) {
                            already.add(pairKey(item._id, cloth._id))
                        }
                    })
                })
                setChecked(already)
                setInitiallyChecked(already)
                setLoading(false)
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, providerId])

    const clothOptions = useMemo(() => cloths.map((c) => ({ value: c._id, label: c.name })), [cloths])
    const colorTypeOptions = useMemo(() => colorTypes.map((c) => ({ value: c._id, label: c.name })), [colorTypes])

    const filteredColors = useMemo(() => {
        const searchLowerCase = search.trim().toLowerCase()
        return colors.filter((item) => {
            const matchSearch = !searchLowerCase || item.name?.toLowerCase().includes(searchLowerCase)
            const matchCloth = !clothFilter || item.cloths?.some((c) => c._id === clothFilter.value)
            const matchColorType = !colorTypeFilter || item.colorType?._id === colorTypeFilter.value
            const matchNoCost = !onlyNoCost || !item.costByCloth?.some((e) => e.costs?.length > 0)
            return matchSearch && matchCloth && matchColorType && matchNoCost
        })
    }, [colors, search, clothFilter, colorTypeFilter, onlyNoCost])

    const groups = useMemo(() => {
        // Uma cor pode existir em vários tecidos — entra em cada grupo de tecido a que pertence.
        const list = []
        const indexByCloth = {}
        filteredColors.forEach((item) => {
            clothSlotsFor(item).forEach((cloth) => {
                const key = cloth._id
                if (indexByCloth[key] === undefined) {
                    indexByCloth[key] = list.length
                    list.push({ clothId: key, clothName: cloth.name, items: [] })
                }
                list[indexByCloth[key]].items.push(item)
            })
        })
        list.forEach((g) => g.items.sort((a, b) => a.name.localeCompare(b.name)))
        list.sort((a, b) => a.clothName.localeCompare(b.clothName))
        return list
    }, [filteredColors])

    const toggleItem = (colorId, clothId) => {
        const key = pairKey(colorId, clothId)
        setChecked((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const markAllInGroup = (group) => {
        setChecked((prev) => {
            const next = new Set(prev)
            group.items.forEach((item) => next.add(pairKey(item._id, group.clothId)))
            return next
        })
    }

    const selectedCount = checked.size

    async function submit() {
        const toAdd = Array.from(checked).filter((key) => !initiallyChecked.has(key))
        const toRemove = Array.from(initiallyChecked).filter((key) => !checked.has(key))

        if (!toAdd.length && !toRemove.length) {
            setFlashMessage('Nenhuma alteração para salvar', 'error')
            return
        }

        const toItems = (keys) => keys.map((key) => {
            const [color, clothId] = key.split('|')
            return { color, cloth: clothId === 'sem-tecido' ? null : clothId }
        })

        setSaving(true)
        let msgType = 'success'
        let message = ''

        if (toAdd.length) {
            const data = await api.post('/color/bulk-apply-cost', { cost: cost._id, items: toItems(toAdd) }, {
                headers: { Authorization: `Bearer ${JSON.parse(token)}` },
            }).then((response) => response.data)
                .catch((err) => { msgType = 'error'; return err.response.data })
            message = data.message
        }

        if (msgType !== 'error' && toRemove.length) {
            const data = await api.post('/color/bulk-remove-cost', { cost: cost._id, items: toItems(toRemove) }, {
                headers: { Authorization: `Bearer ${JSON.parse(token)}` },
            }).then((response) => response.data)
                .catch((err) => { msgType = 'error'; return err.response.data })
            message = data.message
        }

        setSaving(false)
        setFlashMessage(message, msgType)
        if (msgType !== 'error') {
            handleOnSuccess()
        }
    }

    return (
        <FormLayout
            title="Aplicar Custo a Cores"
            actions={
                <>
                    <Button variant="save" onClick={submit} disabled={saving || loading}>Salvar</Button>
                    <Button variant="cancel" onClick={btCancelAction}>Cancelar</Button>
                    <span className="actc-count">{selectedCount} selecionada(s)</span>
                </>
            }
            headerExtra={
                <>
                    <p className="actc-cost-hint">
                        {cost.description && `${cost.description} — `}
                        Entre {cost.minUnit} e {cost.maxUnit} unidades: R$ {NumberToMoney(cost.cost)}
                    </p>
                    <div className="actc-filters">
                        <div className="actc-filter-search">
                            <Input
                                type="text"
                                name="search"
                                placeholder="Buscar por nome da cor..."
                                value={search}
                                handleOnChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="actc-filter-select">
                            <Select
                                isClearable
                                options={clothOptions}
                                styles={SelectStyles}
                                onChange={setClothFilter}
                                value={clothFilter}
                                placeholder="Filtrar por tecido..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                        <div className="actc-filter-select">
                            <Select
                                isClearable
                                options={colorTypeOptions}
                                styles={SelectStyles}
                                onChange={setColorTypeFilter}
                                value={colorTypeFilter}
                                placeholder="Filtrar por tipo de cor..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                        <button
                            type="button"
                            className={`actc-filter-nocost-btn ${onlyNoCost ? 'actc-filter-nocost-btn-active' : ''}`}
                            onClick={() => setOnlyNoCost((v) => !v)}
                        >
                            <FiAlertTriangle size={12} />
                            Sem custo
                        </button>
                    </div>
                </>
            }
        >
            {loading ? (
                <p className="pg-loading">Carregando cores...</p>
            ) : groups.length === 0 ? (
                <p className="actc-hint">Nenhuma cor encontrada para esse filtro.</p>
            ) : (
                <div className="actc-groups">
                    {groups.map((group) => (
                        <div key={group.clothId} className="actc-group">
                            <div className="actc-group-header">
                                <p className="actc-group-title"><FaTshirt size={13} /> {group.clothName}</p>
                                <button type="button" className="actc-mark-all" onClick={() => markAllInGroup(group)}>Marcar todas</button>
                            </div>
                            <div className="actc-checkbox-list">
                                {group.items.map((item) => {
                                    const key = pairKey(item._id, group.clothId)
                                    const wasAlreadyLinked = initiallyChecked.has(key)
                                    const isChecked = checked.has(key)
                                    const clothIdForLookup = group.clothId === 'sem-tecido' ? null : group.clothId
                                    const rowCosts = getCostForCloth(item, clothIdForLookup)
                                    return (
                                        <div
                                            key={key}
                                            className="actc-checkbox-row"
                                            onClick={() => toggleItem(item._id, group.clothId)}
                                        >
                                            <div className="actc-row-main">
                                                <span className={`actc-checkbox ${isChecked ? 'actc-checkbox-checked' : ''}`}>
                                                    {isChecked && <FiCheck size={12} />}
                                                </span>
                                                <span className="actc-checkbox-label">{item.name}</span>
                                                {item.colorType?.name && (
                                                    <span className="actc-checkbox-type">{item.colorType.name}</span>
                                                )}
                                                {wasAlreadyLinked && isChecked && <span className="actc-already-tag">já vinculado</span>}
                                                {wasAlreadyLinked && !isChecked && <span className="actc-will-remove-tag">será desvinculado</span>}
                                            </div>
                                            {rowCosts.length === 0 ? (
                                                <div className="actc-row-costs">
                                                    <span className="actc-no-cost-tag">
                                                        <FiAlertTriangle className="actc-no-cost-icon" size={13} />
                                                        Sem custo definido
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="actc-row-costs">
                                                    {rowCosts.map((c) => (
                                                        <span className="actc-row-cost-item" key={c._id}>
                                                            {c.description && `${c.description} — `}
                                                            {c.minUnit}–{c.maxUnit} un: R$ {NumberToMoney(c.cost)}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </FormLayout>
    )
}

export default ApplyCostToColors
